import { store } from '../redux/store';
import {
  fetchUnreadCount,
  receiveNewMessage,
  receiveReadReceipt,
  removeTypingUser,
  setOnlineUsers,
  setTypingUser,
  updateUserPresence
} from '../redux/features/messages/messagesSlice';
import { BASE_END_POINT_MESSAGES_WS } from '../config';
import { trackMessageReceived } from '../helpers/analytics';

/**
 * WebSocket service for real-time messaging.
 * Manages WebSocket connection, reconnection, and message handling.
 */
class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.isConnecting = false;
    this.token = null;
  }

  /**
   * Connect to WebSocket server.
   *
   * @param {string} token - JWT token for authentication
   */
  connect(token) {
    if (
      this.isConnecting ||
      (this.ws && this.ws.readyState === WebSocket.OPEN)
    ) {
      console.log('🔌 [WebSocket] Already connected or connecting');
      return;
    }

    this.token = token;
    this.isConnecting = true;

    try {
      const wsUrl = `${BASE_END_POINT_MESSAGES_WS}?token=${encodeURIComponent(
        token
      )}`;
      console.log('🔌 [WebSocket] Connecting to:', wsUrl);

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
    } catch (error) {
      console.error('❌ [WebSocket] Connection error:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  /**
   * Handle WebSocket connection open.
   */
  handleOpen() {
    console.log('✅ [WebSocket] Connected successfully');
    this.isConnecting = false;
    this.reconnectAttempts = 0;

    // Track WebSocket connected. Disabled this tracking at this point since there is too much noice in the GA data we can avoid.
    // trackWebSocketStatus('connected');

    // Refresh unread count on reconnection to ensure we're in sync
    store.dispatch(fetchUnreadCount());
  }

  /**
   * Handle incoming WebSocket message.
   *
   * @param {MessageEvent} event - WebSocket message event
   */
  handleMessage(event) {
    try {
      const data = JSON.parse(event.data);
      console.log('📨 [WebSocket] Received message:', data.type);

      switch (data.type) {
        case 'new_message':
          console.log(
            '🔔 [WebSocket] Dispatching receiveNewMessage:',
            data.message
          );
          store.dispatch(receiveNewMessage({ message: data.message }));
          console.log(
            '🔔 [WebSocket] Current unread count after dispatch:',
            store.getState().messages.totalUnreadCount
          );

          // Track message received
          const currentConversation =
            store.getState().messages.currentConversation;
          const isViewingConversation =
            currentConversation?.id === data.message.conversationId;
          trackMessageReceived(
            data.message.conversationId,
            isViewingConversation
          );
          break;
        case 'read_receipt':
          store.dispatch(
            receiveReadReceipt({ conversationId: data.conversationId })
          );
          break;
        case 'typing':
          if (data.isTyping) {
            store.dispatch(
              setTypingUser({
                conversationId: data.conversationId,
                userId: data.userId
              })
            );
            // Auto-remove after 3 seconds
            setTimeout(() => {
              store.dispatch(
                removeTypingUser({
                  conversationId: data.conversationId,
                  userId: data.userId
                })
              );
            }, 3000);
          } else {
            store.dispatch(
              removeTypingUser({
                conversationId: data.conversationId,
                userId: data.userId
              })
            );
          }
          break;
        case 'online_users':
          console.log(
            '🟢 [WebSocket] Received online users list:',
            data.userIds
          );
          store.dispatch(setOnlineUsers({ userIds: data.userIds }));
          break;
        case 'presence':
          console.log(
            '🟢 [WebSocket] Presence update:',
            data.userId,
            data.isOnline ? 'online' : 'offline'
          );
          store.dispatch(
            updateUserPresence({ userId: data.userId, isOnline: data.isOnline })
          );
          break;
        default:
          console.warn('⚠️ [WebSocket] Unknown message type:', data.type);
      }
    } catch (error) {
      console.error('❌ [WebSocket] Error processing message:', error);
    }
  }

  /**
   * Handle WebSocket error.
   *
   * @param {Event} error - Error event
   */
  handleError(error) {
    console.error('❌ [WebSocket] Error:', error);
    this.isConnecting = false;

    // Track WebSocket error. Disabled this tracking at this point since there is too much noice in the GA data we can avoid.
    // trackWebSocketStatus('error');
  }

  /**
   * Handle WebSocket connection close.
   *
   * @param {CloseEvent} event - Close event
   */
  handleClose(event) {
    console.log('🔌 [WebSocket] Connection closed:', event.code, event.reason);
    this.isConnecting = false;
    this.ws = null;

    // Track WebSocket disconnected. Disabled this tracking at this point since there is too much noice in the GA data we can avoid.
    //trackWebSocketStatus('disconnected');

    // Attempt to reconnect if not intentionally closed
    if (event.code !== 1000 && event.code !== 1001) {
      this.scheduleReconnect();
    }
  }

  /**
   * Schedule a reconnection attempt.
   */
  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ [WebSocket] Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    console.log(
      `🔄 [WebSocket] Reconnecting in ${this.reconnectDelay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`
    );

    // Track reconnection attempt. Disabled this tracking at this point since there is too much noice in the GA data we can avoid.
    // trackWebSocketStatus('reconnecting');

    setTimeout(() => {
      if (this.token) {
        this.connect(this.token);
      }
    }, this.reconnectDelay);
  }

  /**
   * Send typing indicator.
   *
   * @param {string} conversationId - Conversation ID
   * @param {string} recipientUserId - Recipient user ID
   * @param {boolean} isTyping - Whether user is typing
   */
  sendTypingIndicator(conversationId, recipientUserId, isTyping) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const payload = {
        type: 'typing',
        conversationId,
        recipientUserId,
        isTyping
      };
      this.ws.send(JSON.stringify(payload));
      console.log('⌨️ [WebSocket] Sent typing indicator:', isTyping);
    }
  }

  /**
   * Disconnect from WebSocket server.
   */
  disconnect() {
    if (this.ws) {
      console.log('🔌 [WebSocket] Disconnecting...');
      this.ws.close(1000, 'User logged out');
      this.ws = null;
      this.token = null;
      this.reconnectAttempts = 0;
    }
  }

  /**
   * Check if WebSocket is connected.
   *
   * @returns {boolean} Connection status
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

// Export singleton instance
const websocketService = new WebSocketService();
export default websocketService;
